import type { TFunction } from 'i18next';
import { TRANSLATIONS } from './translations';
import { translate } from '../helpers/translationHelper';
import { HeavyDutyMachineryOption } from './heavyDutyMachinerySpaceOptions';

// Curated fixed lists describing the accessory itself -- same
// "always the same fields, all optional, validated server-side against a
// fixed HashSet" pattern as PROPULSION_TYPE_OPTIONS/FUEL_TYPE_OPTIONS
// (machineryGlobalSpecOptions.ts), just for MachineryAccessory instead of
// Machinery. Deliberately not part of the Sector/Family/MachineryType
// taxonomy -- a coupler type or a hydraulic connector isn't a kind of
// machinery, it's a property of the accessory.
export const COUPLER_TYPE_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'Lehnhoff', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_LEHNHOFF },
  { value: 'CwVerachtert', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_CW_VERACHTERT },
  { value: 'UniversalSkidSteer', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_UNIVERSAL_SKID_STEER },
  { value: 'Nordic', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_NORDIC },
  { value: 'PinOn', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_PIN_ON },
  { value: 'Other', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_OTHER },
];

/**
 * Specific models within a CouplerType family -- deliberately a small,
 * explicitly incomplete seed list (the user's own words: "vamos haciendo la
 * lista poco a poco", to be corrected/expanded as real model codes come
 * in), not meant to be exhaustive on day one. Picking a model is optional
 * even for a family listed here -- MyMachineryAccessories.tsx's own
 * grouped Select always keeps the family's own plain value (from
 * COUPLER_TYPE_OPTIONS) selectable as a "generic / no specific model" row
 * inside that same group, so a host who doesn't see their exact model just
 * stays on that instead. Families with no brand-specific models
 * (UniversalSkidSteer/PinOn/Other) simply have no entry here and fall back
 * to their own flat value from COUPLER_TYPE_OPTIONS, same as before this
 * round.
 */
export interface CouplerModelOption {
  value: string;
  labelKey: string;
}

export interface CouplerModelGroup {
  /** Matches one of COUPLER_TYPE_OPTIONS' own `value`s -- the family this group's models belong under. */
  familyValue: string;
  models: CouplerModelOption[];
}

export const COUPLER_MODEL_GROUPS: CouplerModelGroup[] = [
  {
    familyValue: 'Lehnhoff',
    models: [
      { value: 'Lehnhoff_MS01', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_LEHNHOFF_MS01 },
      { value: 'Lehnhoff_MS03', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_LEHNHOFF_MS03 },
      { value: 'Lehnhoff_MS08', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_LEHNHOFF_MS08 },
    ],
  },
  {
    familyValue: 'CwVerachtert',
    models: [
      { value: 'CwVerachtert_CW05', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_CW_VERACHTERT_CW05 },
      { value: 'CwVerachtert_CW10', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_CW_VERACHTERT_CW10 },
      { value: 'CwVerachtert_CW20', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_CW_VERACHTERT_CW20 },
    ],
  },
  {
    familyValue: 'Nordic',
    models: [
      { value: 'Nordic_S30', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_NORDIC_S30 },
      { value: 'Nordic_S40', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_NORDIC_S40 },
      { value: 'Nordic_S60', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_MODEL_NORDIC_S60 },
    ],
  },
];

export const HYDRAULIC_LINES_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'OneLine', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_HYDRAULIC_LINES_ONE },
  { value: 'TwoLines', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_HYDRAULIC_LINES_TWO },
];

export const HYDRAULIC_CONNECTOR_TYPE_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'FlatFace', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_HYDRAULIC_CONNECTOR_FLAT_FACE },
  { value: 'Threaded', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_HYDRAULIC_CONNECTOR_THREADED },
  { value: 'Standard', labelKey: TRANSLATIONS.MACHINERY_ACCESSORY_HYDRAULIC_CONNECTOR_STANDARD },
];

/**
 * Groups each CouplerType family (Lehnhoff, CW/Verachtert, Nordic...) with
 * its own curated models underneath -- see COUPLER_MODEL_GROUPS's own doc
 * comment. Shared by MyMachineryAccessories.tsx's create/edit form and
 * AccessoryFiltersModal's own CouplerType filter, so both build the exact
 * same antd Select `options` shape (plain entries mixed with grouped
 * `optgroup` entries) from a single place instead of duplicating this
 * grouping logic.
 */
export const buildCouplerTypeGroupedOptions = (t: TFunction) =>
  COUPLER_TYPE_OPTIONS.map((familyOpt) => {
    const familyLabel = translate(t, familyOpt.labelKey);
    const group = COUPLER_MODEL_GROUPS.find((g) => g.familyValue === familyOpt.value);
    if (!group) return { label: familyLabel, value: familyOpt.value };
    return {
      label: familyLabel,
      options: [
        { label: translate(t, TRANSLATIONS.MACHINERY_ACCESSORY_COUPLER_GENERIC_OPTION), value: familyOpt.value },
        ...group.models.map((m) => ({ label: translate(t, m.labelKey), value: m.value })),
      ],
    };
  });
